import React from "react";
import {
  Box,
  Typography,
  useTheme,
  Paper,
} from "@mui/material";

const CodeDiffViewer = ({ originalCode, fixedCode, fileName }) => {
  const theme = useTheme();

  const stripHtmlTags = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const getOriginalCodeFromSnippet = (issueDetails) => {
    if (!Array.isArray(issueDetails)) return { code: "", lineNumbers: [] };
    
    let allCodeLines = [];
    issueDetails.forEach((issue) => {
      const codeLines = issue.code_snippet?.code_lines || [];
      codeLines.forEach((line) => {
        allCodeLines.push({
          lineNumber: line.line_number,
          code: stripHtmlTags(line.code || "")
        });
      });
    });

    // Sort by line number
    allCodeLines.sort((a, b) => a.lineNumber - b.lineNumber);
    return {
      code: allCodeLines.map(line => line.code).join('\n'),
      lineNumbers: allCodeLines.map(line => line.lineNumber)
    };
  };

  // Simple diff algorithm to identify changes with preserved line numbers
  const computeDiff = (originalData, fixed) => {
    const originalLines = originalData.code.split('\n');
    const originalLineNumbers = originalData.lineNumbers;
    const fixedLines = fixed.split('\n');
    
    const diff = [];
    let originalIndex = 0;
    let fixedIndex = 0;

    while (originalIndex < originalLines.length || fixedIndex < fixedLines.length) {
      const originalLine = originalLines[originalIndex];
      const fixedLine = fixedLines[fixedIndex];
      const currentOriginalLineNumber = originalLineNumbers[originalIndex];

      if (originalIndex >= originalLines.length) {
        // Only fixed lines remaining (additions)
        const newLineNumber = originalLineNumbers[originalLineNumbers.length - 1] + (fixedIndex - originalLines.length) + 1;
        diff.push({
          type: 'added',
          content: fixedLine,
          originalLineNumber: null,
          fixedLineNumber: newLineNumber,
        });
        fixedIndex++;
      } else if (fixedIndex >= fixedLines.length) {
        // Only original lines remaining (deletions)
        diff.push({
          type: 'deleted',
          content: originalLine,
          originalLineNumber: currentOriginalLineNumber,
          fixedLineNumber: null,
        });
        originalIndex++;
      } else if (originalLine === fixedLine) {
        // Lines are the same
        diff.push({
          type: 'unchanged',
          content: originalLine,
          originalLineNumber: currentOriginalLineNumber,
          fixedLineNumber: currentOriginalLineNumber,
        });
        originalIndex++;
        fixedIndex++;
      } else {
        // Lines are different - check if it's a modification or addition/deletion
        let foundMatch = false;
        
        // Look ahead to see if the fixed line appears later in original
        for (let i = originalIndex + 1; i < Math.min(originalIndex + 5, originalLines.length); i++) {
          if (originalLines[i] === fixedLine) {
            // The fixed line exists later in original, so current original line was deleted
            diff.push({
              type: 'deleted',
              content: originalLine,
              originalLineNumber: currentOriginalLineNumber,
              fixedLineNumber: null,
            });
            originalIndex++;
            foundMatch = true;
            break;
          }
        }
        
        if (!foundMatch) {
          // Look ahead to see if the original line appears later in fixed
          for (let i = fixedIndex + 1; i < Math.min(fixedIndex + 5, fixedLines.length); i++) {
            if (fixedLines[i] === originalLine) {
              // The original line exists later in fixed, so current fixed line was added
              const newLineNumber = currentOriginalLineNumber;
              diff.push({
                type: 'added',
                content: fixedLine,
                originalLineNumber: null,
                fixedLineNumber: newLineNumber,
              });
              fixedIndex++;
              foundMatch = true;
              break;
            }
          }
        }
        
        if (!foundMatch) {
          // Lines are different, treat as modification (delete + add)
          diff.push({
            type: 'deleted',
            content: originalLine,
            originalLineNumber: currentOriginalLineNumber,
            fixedLineNumber: null,
          });
          diff.push({
            type: 'added',
            content: fixedLine,
            originalLineNumber: null,
            fixedLineNumber: currentOriginalLineNumber,
          });
          originalIndex++;
          fixedIndex++;
        }
      }
    }

    return diff;
  };

  const originalCodeData = typeof originalCode === 'string' ? 
    { code: originalCode, lineNumbers: originalCode.split('\n').map((_, i) => i + 1) } : 
    getOriginalCodeFromSnippet(originalCode);
  const diffLines = computeDiff(originalCodeData, fixedCode);

  const getBackgroundColor = (type) => {
    switch (type) {
      case 'added':
        return theme.palette.mode === 'dark' ? '#0d4429' : '#e6ffed';
      case 'deleted':
        return theme.palette.mode === 'dark' ? '#5a1e1e' : '#ffe6e6';
      case 'unchanged':
        return 'transparent';
      default:
        return 'transparent';
    }
  };

  const getBorderColor = (type) => {
    switch (type) {
      case 'added':
        return theme.palette.success.main;
      case 'deleted':
        return theme.palette.error.main;
      default:
        return 'transparent';
    }
  };

  const getLinePrefix = (type) => {
    switch (type) {
      case 'added':
        return '+';
      case 'deleted':
        return '-';
      default:
        return ' ';
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ 
        mb: 2, 
        color: theme.palette.primary.main,
        fontWeight: 'bold'
      }}>
        🔄 Code Diff - AI Generated Fix
      </Typography>
      
      <Typography variant="body2" sx={{ 
        mb: 3, 
        color: theme.palette.text.secondary 
      }}>
        {fileName && `File: ${fileName} | `}
        <span style={{ color: theme.palette.error.main }}>Red lines</span> show removed code, 
        <span style={{ color: theme.palette.success.main }}> green lines</span> show added code.
      </Typography>

      <Paper 
        elevation={2}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f8f9fa',
            color: theme.palette.text.primary,
            padding: "8px 16px",
            fontSize: "12px",
            fontWeight: "bold",
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          📝 Code Changes
        </Box>
        
        <Box
          sx={{
            maxHeight: "600px",
            overflow: "auto",
            backgroundColor: theme.palette.mode === "dark" ? "#0d1117" : "#ffffff",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {diffLines.map((line, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  backgroundColor: getBackgroundColor(line.type),
                  borderLeft: `3px solid ${getBorderColor(line.type)}`,
                  minHeight: "20px",
                  '&:hover': {
                    backgroundColor: line.type === 'unchanged' 
                      ? (theme.palette.mode === 'dark' ? '#1c1c1c' : '#f5f5f5')
                      : undefined
                  }
                }}
              >
                {/* Line Numbers */}
                <Box
                  sx={{
                    display: "flex",
                    backgroundColor: theme.palette.mode === "dark" ? "#161b22" : "#f6f8fa",
                    borderRight: `1px solid ${theme.palette.divider}`,
                    userSelect: "none",
                    minWidth: "80px",
                  }}
                >
                  {/* Original line number */}
                  <Box
                    sx={{
                      padding: "2px 8px",
                      minWidth: "35px",
                      textAlign: "right",
                      fontSize: "12px",
                      color: theme.palette.text.secondary,
                      fontFamily: "monospace",
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {line.type !== 'added' ? (line.originalLineNumber || '') : ''}
                  </Box>
                  {/* Fixed line number */}
                  <Box
                    sx={{
                      padding: "2px 8px",
                      minWidth: "35px",
                      textAlign: "right",
                      fontSize: "12px",
                      color: theme.palette.text.secondary,
                      fontFamily: "monospace",
                    }}
                  >
                    {line.type !== 'deleted' ? (line.fixedLineNumber || '') : ''}
                  </Box>
                </Box>

                {/* Change indicator */}
                <Box
                  sx={{
                    padding: "2px 8px",
                    minWidth: "20px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    fontFamily: "monospace",
                    color: 
                      line.type === 'added' ? theme.palette.success.main :
                      line.type === 'deleted' ? theme.palette.error.main :
                      theme.palette.text.disabled,
                    backgroundColor: theme.palette.mode === "dark" ? "#0d1117" : "#ffffff",
                  }}
                >
                  {getLinePrefix(line.type)}
                </Box>

                {/* Code Content */}
                <Box sx={{ 
                  padding: "2px 12px", 
                  flex: 1,
                  fontFamily: '"Courier New", Courier, monospace',
                  fontSize: "13px",
                  lineHeight: "1.4",
                  color: 
                    line.type === 'added' ? theme.palette.success.dark :
                    line.type === 'deleted' ? theme.palette.error.dark :
                    theme.palette.text.primary,
                }}>
                  <Typography
                    component="span"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      textDecoration: line.type === 'deleted' ? 'line-through' : 'none',
                    }}
                  >
                    {line.content || " "}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Summary Box */}
      <Box sx={{ 
        mt: 3, 
        p: 2, 
        backgroundColor: theme.palette.mode === 'dark' ? '#1a2332' : '#e3f2fd',
        borderRadius: 1,
        border: `1px solid ${theme.palette.info.light}`,
      }}>
        <Typography variant="body2" sx={{ 
          fontWeight: 'bold',
          color: theme.palette.info.main,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          💡 AI Fix Summary
        </Typography>
        <Typography variant="body2" sx={{ 
          mt: 1,
          color: theme.palette.text.secondary 
        }}>
          The diff above shows exactly what the AI changed to fix the issue. 
          Lines with <strong style={{ color: theme.palette.error.main }}>red background (-)</strong> were removed, 
          lines with <strong style={{ color: theme.palette.success.main }}>green background (+)</strong> were added.
          Review the changes and create a Pull Request if you approve.
        </Typography>
      </Box>
    </Box>
  );
};

export default CodeDiffViewer;